import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseQueryOptions, useQuery, useQueryClient } from 'react-query'
import { AIJobStatus, BrandKitShow } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

export interface Data {
  brand_kit: BrandKitShow
}

type CacheData = BrandKitShow[] | undefined

export const fetchBrandKit = async ({
  id,
  workspaceId,
  signal,
  includeAllMediaFiles,
  includeBrandVisualAnalysis,
}: {
  id: number
  workspaceId: number
  signal?: AbortSignal
  includeAllMediaFiles?: boolean
  includeBrandVisualAnalysis?: boolean
}) => {
  const params = {
    include_all_media_files: includeAllMediaFiles,
    include_brand_visual_analysis: includeBrandVisualAnalysis,
  }
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits/${id}`, { signal, params })
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export const useBrandKit = (
  id?: number,
  options?: UseQueryOptions<Data> & {
    includeAllMediaFiles?: boolean
    includeBrandVisualAnalysis?: boolean
    /**
     * Set to `true` to poll every 500 ms until the brand-kit job reaches
     * a terminal status (Successful | Failed | Expired).
     * Default is `false` (no polling).
     */
    pollUntilFinal?: boolean
    /**
     * Set to `true` to poll every 500 ms until `bad_image_detection_completed`
     * is true on the brand kit.
     * Default is `false` (no polling).
     */
    pollUntilBadImageDetection?: boolean
  },
) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  const {
    pollUntilFinal = false,
    pollUntilBadImageDetection = false,
    includeAllMediaFiles,
    includeBrandVisualAnalysis,
    ...queryOptions
  } = options ?? {}

  const pollingInterval = (data: Data | undefined) => {
    if (pollUntilBadImageDetection) {
      return data?.brand_kit.bad_image_detection_completed ? false : 500
    }
    if (!pollUntilFinal) return false
    if (!data) return 500
    const status = data.brand_kit.status as AIJobStatus
    return [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired].includes(status) ? false : 500
  }

  return useQuery<Data>(
    [
      'workspaces',
      workspaceId,
      'brandKits',
      'detail',
      id ?? null,
      {
        includeAllMediaFiles: includeAllMediaFiles || false,
        includeBrandVisualAnalysis: includeBrandVisualAnalysis || false,
      },
    ],
    () => {
      if (!id) throw new Error('No id provided')
      return fetchBrandKit({ id, workspaceId, includeAllMediaFiles, includeBrandVisualAnalysis })
    },
    {
      placeholderData: () => {
        const cacheData = queryClient.getQueryData<CacheData>(['workspaces', workspaceId, 'brandKits', 'list'])
        const existingKit = cacheData?.find((bk) => bk.id === id)
        return existingKit ? { brand_kit: existingKit } : undefined
      },
      refetchInterval: queryOptions.refetchInterval ?? pollingInterval,
      refetchOnWindowFocus: false,
      staleTime: (queryOptions.cacheTime || 0) * 0.5,
      enabled: !!id,
      ...queryOptions,
    },
  )
}
